import {
  Avatar,
  Box,
  Button,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { lighten } from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EventLocation } from "types";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { useAuthorization } from "context/AuthorizationContext";
import { FmdGoodRounded } from "@mui/icons-material";

interface DetailsModalProps {
  open: boolean;
  locationId: string;
  layout?: "center" | "fullscreen";
  onClose: () => void;
}

const LocationDetailsModal: React.FC<DetailsModalProps> = ({
  open,
  locationId,
  layout,
  onClose,
}) => {
  const [location, setLocation] = useState<EventLocation>({
    id: "",
    description: "",
    address: "",
    city: "",
    country: "",
    province: "",
    region: "",
    zip: "",
    createdAt: "",
    whoCreate: "",
    updatedAt: null,
    whoUpdate: null,
    deletedAt: null,
  });
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { isAllowed } = useAuthorization();

  const fetchLocation = useCallback(async () => {
    try {
      const { data } = await axios.get<{ data: EventLocation }>(
        `/api/locations/${locationId}`
      );
      setLocation(data.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/logout", { replace: true });
          return;
        }
        console.log(error.response?.data);
      } else {
        console.log(error);
      }
    }
  }, [locationId, navigate]);

  useEffect(() => {
    fetchLocation();
  }, [locationId, fetchLocation]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          p: 0,
          minWidth: layout && layout === "fullscreen" ? undefined : 400,
          bgcolor: (theme) => lighten(theme.palette.primary[200], 0.9),
        }}
        layout={layout ? layout : "center"}
        size="lg"
        aria-labelledby="location-detail-modal-title"
        aria-describedby="location-detail-modal-description"
      >
        <Box
          display="flex"
          flexDirection="column"
          width={"100%"}
          height={"100%"}
        >
          <ModalClose color="neutral" variant="plain" />
          {/* Modal body */}
          <Box flex={"1 1"} width={"100%"} height={"100%"}>
            {/* Artist main info container */}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent={"flex-start"}
              alignItems={"center"}
              gap={2}
              p={2}
              height={100}
            >
              <Avatar
                color="primary"
                size="lg"
                sx={{ width: "5rem", height: "5rem" }}
              >
                <FmdGoodRounded fontSize="large" />
              </Avatar>

              <Box display="flex" flexDirection="column">
                <Typography level="h6">{location.description}</Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              flex={"1 1"}
              height={"calc(100% - 100px)"}
              rowGap={1}
              bgcolor={(theme) => theme.palette.background.body}
              p={2}
              boxSizing={"border-box"}
            >
              <Box
                display="flex"
                flexDirection="row"
                columnGap={2}
                justifyContent={"space-between"}
                width={"100%"}
              >
                {/* Role */}
                <Box display="flex" flexDirection="column">
                  <Typography level="body2" fontWeight={600}>
                    {t("Address")}
                  </Typography>
                  <Typography level="body1">{`${location.address} ${location.zip}`}</Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                columnGap={2}
                justifyContent={"space-between"}
                width={"100%"}
              >
                {/* Role */}
                <Box display="flex" flexDirection="column">
                  <Typography level="body2" fontWeight={600}>
                    {t("City")}
                  </Typography>
                  <Typography level="body1">{`${location.city} (${location.province})`}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          {/* Modal footer */}
          <Box
            display="flex"
            flexDirection="row"
            bgcolor={(theme) => theme.palette.background.body}
            borderRadius={
              layout && layout === "fullscreen" ? 0 : "0px 0px 10px 10px"
            }
            p={2}
            justifyContent="flex-end"
          >
            {isAllowed("locations.write") ? (
              <Button
                variant="plain"
                color="neutral"
                startDecorator={<EditIcon />}
                size="sm"
                onClick={() => {
                  navigate(locationId);
                }}
              >
                {t("Edit")}
              </Button>
            ) : null}
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default LocationDetailsModal;
